import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Button, Tooltip, Switch } from "antd";
import {
  CartesianGrid,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

import removeSVG from "../../assets/close.svg";
import addSVG from "../../assets/add_item.svg";
import { useEditableCell } from "../../hooks/useEditableTableCell.js";
import {
  calculateBlackScholesValue,
  calculateRemainingGrantOptionValues,
  calculateRemainingGrantRSUValues,
  getUpdatedTermLengths,
} from "./_10b51utils";
import {
  toDollar,
  trancheOptionAstericksMessage,
} from "../../utils/Constants/systemSetting.js";

export default function AwardsTable({
  companyInfo,
  awardTitle,
  paramTableRows,
  awardCalculatorValues,
  setAwardCalculatorValues,
  generalInfoParams = {},
  taxTableParams = {},
  trancheTableValues = {},
  headless = false,
  updateNeeded = false,
  setUpdateNeeded = () => null,
  trancheScheduleSelected = "",
  calculatorPageSelected = "",
  setAllTrancheTableValues = () => null,
  settrancheScheduleSelected = () => null,
  includeTaxes = false,
  abbreviatedTable = false,
  hidden = false,
  currentStockPrice = 0,
}) {
  const renderEditableCell = useEditableCell();

  const [blackScholesUpdateNeeded, setBlackScholesUpdateNeeded] =
    useState(false);
  const [RSUvalueUpdateNeeded, setRSUvalueUpdateNeeded] = useState(false);
  const [remainingGrantsUpdateNeeded, setRemainingGrantsUpdateNeeded] =
    useState(false);

  // Initialization function to populate the first award's values
  useEffect(() => {
    if (!awardCalculatorValues?.length) {
      const optionsCalculatorDefaultValues = Object.fromEntries(
        Object.values(paramTableRows).map((row) => [
          row.key,
          row.key === "ID"
            ? "grant1"
            : row?.defaultValue !== ""
              ? row?.defaultValue
              : "",
        ]),
      );
      setAwardCalculatorValues([optionsCalculatorDefaultValues]);
    }
  }, [paramTableRows, awardCalculatorValues, setAwardCalculatorValues]);

  // Function for updating the options values
  const updateOptionParams = useCallback(
    (ID, record, newValue) => {
      const awardIndex = awardCalculatorValues.findIndex(
        (item) => item.ID === ID,
      );
      const newOptionsValues = [...awardCalculatorValues];
      const newOptionAwardValues = {
        ...awardCalculatorValues[awardIndex],
        [record.key]: newValue,
      };
      newOptionsValues[awardIndex] = newOptionAwardValues;

      setAwardCalculatorValues(newOptionsValues);
      setBlackScholesUpdateNeeded(true);
      setRemainingGrantsUpdateNeeded(true);
    },
    [setAwardCalculatorValues, awardCalculatorValues],
  );

  // Trigger black-scholes valuation update for both general params (like current stock price)
  // and for updated award values (as seen above)
  useEffect(() => {
    if (generalInfoParams?.[0]) {
      setBlackScholesUpdateNeeded(true);
      setRSUvalueUpdateNeeded(true);
    }
  }, [generalInfoParams]);

  const [prevTotalUnits, setPrevTotalUnits] = useState({});
  useEffect(() => {
    if (trancheTableValues?.["Grant 1"]) {
      Object.entries(trancheTableValues).forEach(
        ([trancheKey, trancheValues]) => {
          const remainingUnitsTotal = trancheValues.reduce(
            (prev, curr) => prev + (parseInt(curr?.remainingUnits) || 0),
            0,
          );
          if (remainingUnitsTotal !== prevTotalUnits?.[trancheKey]) {
            setPrevTotalUnits((prev) => {
              const newValues = { ...prev };
              newValues[trancheKey] = remainingUnitsTotal;
              return newValues;
            });
            setRemainingGrantsUpdateNeeded(true);
          }
        },
      );
    }
  }, [trancheTableValues, prevTotalUnits]);

  useEffect(() => {
    if (updateNeeded) {
      setRemainingGrantsUpdateNeeded(true);
      setBlackScholesUpdateNeeded(true);
      setUpdateNeeded(false);
    }
  }, [updateNeeded, setUpdateNeeded]);

  useEffect(() => {
    setRemainingGrantsUpdateNeeded(true);
  }, [taxTableParams]);

  // Calculate Black-Scholes for the Option Calculator based on currentAwardDetails and generalInfoParams
  useEffect(() => {
    if (
      awardTitle === "Option" &&
      generalInfoParams?.[0] &&
      (blackScholesUpdateNeeded || remainingGrantsUpdateNeeded)
    ) {
      const newOptionsValues = [...awardCalculatorValues];
      awardCalculatorValues.forEach((newOptionAwardValues, awardIndex) => {
        const updatedTermLengths = getUpdatedTermLengths({
          ...generalInfoParams?.[0],
          ...newOptionAwardValues,
        });
        newOptionAwardValues = {
          ...newOptionAwardValues,
          ...updatedTermLengths,
        };

        const newCalculatedBlackScholesValues = calculateBlackScholesValue({
          ...generalInfoParams?.[0],
          ...newOptionAwardValues,
        });
        const newBlackScholesValues = {
          optionValue: newCalculatedBlackScholesValues?.callPriceEstimate,
          nD1: newCalculatedBlackScholesValues?.nD1
            ? newCalculatedBlackScholesValues.nD1 * 100
            : "",
        };
        const remainingGrantValues = calculateRemainingGrantOptionValues(
          { ...newOptionAwardValues, ...newBlackScholesValues },
          trancheTableValues?.[
            newOptionAwardValues.ID.replace("grant", "Grant ")
          ],
          generalInfoParams,
          taxTableParams,
        );
        newOptionsValues[awardIndex] = {
          ...newOptionAwardValues,
          ...newBlackScholesValues,
          ...remainingGrantValues,
        };
      });
      setAwardCalculatorValues(newOptionsValues);
      setBlackScholesUpdateNeeded(false);
      setRemainingGrantsUpdateNeeded(false);
    }
  }, [
    awardTitle,
    generalInfoParams,
    taxTableParams,
    trancheTableValues,
    setAwardCalculatorValues,
    awardCalculatorValues,
    blackScholesUpdateNeeded,
    remainingGrantsUpdateNeeded,
    updateNeeded,
  ]);

  useEffect(() => {
    if (
      awardTitle === "RSU" &&
      generalInfoParams?.[0] &&
      (RSUvalueUpdateNeeded || remainingGrantsUpdateNeeded)
    ) {
      const newRSUtableValues = [...awardCalculatorValues];
      awardCalculatorValues.forEach((newRSUawardValues, awardIndex) => {
        const updatedTermLengths = getUpdatedTermLengths({
          ...generalInfoParams?.[0],
          ...newRSUawardValues,
        });
        newRSUawardValues = {
          ...newRSUawardValues,
          ...updatedTermLengths,
        };

        const remainingGrantValues = calculateRemainingGrantRSUValues(
          newRSUawardValues,
          generalInfoParams[0],
          trancheTableValues?.[newRSUawardValues.ID.replace("grant", "Grant ")],
          taxTableParams,
        );
        newRSUtableValues[awardIndex] = {
          ...newRSUawardValues,
          ...remainingGrantValues,
        };
      });
      setAwardCalculatorValues(newRSUtableValues);
      setRemainingGrantsUpdateNeeded(false);
      setRSUvalueUpdateNeeded(false);
    }
  }, [
    awardTitle,
    generalInfoParams,
    taxTableParams,
    trancheTableValues,
    setAwardCalculatorValues,
    awardCalculatorValues,
    remainingGrantsUpdateNeeded,
    RSUvalueUpdateNeeded,
  ]);

  const [trancheRemovalNeededForID, setTrancheRemovalNeededForID] =
    useState("");

  // Function for removing an award column (X button on frontend)
  const handleRemove = useCallback(
    (key) => {
      setParamsColumns((prevData) => {
        return prevData.filter((item) => item.key !== key);
      });
      setAwardCalculatorValues((prevData) => {
        return prevData.filter((item) => item.ID !== key);
      });
      setTrancheRemovalNeededForID(key);
    },
    [setAwardCalculatorValues],
  );

  useEffect(() => {
    if (trancheRemovalNeededForID) {
      setAllTrancheTableValues((prev) => {
        return {
          ...prev,
          [calculatorPageSelected]: Object.fromEntries(
            Object.entries(prev[calculatorPageSelected]).filter(
              ([key, val]) =>
                key !== trancheRemovalNeededForID.replace("grant", "Grant "),
            ),
          ),
        };
      });
      settrancheScheduleSelected("Grant 1");
    }
    setTrancheRemovalNeededForID("");
  }, [
    trancheRemovalNeededForID,
    calculatorPageSelected,
    trancheScheduleSelected,
    setAllTrancheTableValues,
    settrancheScheduleSelected,
  ]);

  // Set defintion of basic column(s)
  const defaultColumns = [
    {
      title: `${awardTitle} Parameter`,
      dataIndex: "key",
      key: "key",
      align: "center",
      render: (text, record) => record.title,
    },
  ];
  const [paramsColumns, setParamsColumns] = useState([...defaultColumns]);

  // Add/remove award columns based on the current values' updates.
  // Also initializes the first grant column based on awardCalculatorValues initialization above
  useMemo(() => {
    if (!awardCalculatorValues) return;
    setParamsColumns((prev) => {
      const currentAwards = awardCalculatorValues
        .filter(
          (award) =>
            !prev.some((existingAward) => award.ID === existingAward.key),
        )
        .map((award) => ({
          title: (
            <div>
              <p style={{ textAlign: "center" }}>
                {award.ID.replace("grant", "Grant ")}
              </p>{" "}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Tooltip title="Remove Award?">
                  <Button
                    cursor="pointer"
                    type="link"
                    onClick={() => handleRemove(`${award.ID}`)}
                  >
                    <img src={removeSVG} alt="remove" width={11} />
                  </Button>
                </Tooltip>
              </div>
            </div>
          ),
          key: `${award.ID}`,
          align: "center",
        }));
      return [
        ...prev.filter((item) => item.key !== "addAward"),
        ...currentAwards,
      ];
    });
  }, [handleRemove, awardCalculatorValues]);

  // Function for adding an award column (+ button on frontend)
  const handleAddColumn = useCallback(() => {
    // Add object to current array of values
    const addAwardColumn = (grantID) => {
      const newValues = Object.fromEntries(
        Object.values(paramTableRows).map((row) => [
          row.key,
          row.key === "ID" ? grantID : "",
        ]),
      );
      setAwardCalculatorValues((prev) => [...prev, newValues]);
    };

    // Add column itself (with ability to remove itself)
    setParamsColumns((prevData) => {
      const numberOfColumns = prevData.length - 1;
      const grantID = `grant${numberOfColumns}`;
      addAwardColumn(grantID);

      const newColumn = {
        title: (
          <div>
            <p style={{ textAlign: "center" }}>Grant {numberOfColumns}</p>{" "}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip title="Remove Award?">
                <Button
                  cursor="pointer"
                  type="link"
                  onClick={() => handleRemove(grantID)}
                >
                  <img src={removeSVG} alt="remove" width={11} />
                </Button>
              </Tooltip>
            </div>
          </div>
        ),
        key: grantID,
        align: "center",
      };

      return [...prevData.filter((item) => item.key !== "addAward"), newColumn];
    });
  }, [paramTableRows, handleRemove, setAwardCalculatorValues]);

  const [currentEditedCell, setCurrentEditedCell] = useState("");

  // Function to add a render function to each column (to try to keep the ref in memory current)
  const paramTableRowsRendered = useMemo(
    () =>
      paramsColumns.map((col) => {
        const currentAwardsValues = awardCalculatorValues?.find(
          (item) => item.ID === col.key,
        );

        if (col.key.includes("grant")) {
          col = {
            ...col,
            render: (text, record) => {
              const cellKey = `${record.key}-${col.key}`;
              const isCurrentEditedCell = currentEditedCell === cellKey;

              const currentAssignedValue = currentAwardsValues?.[record.key];

              const value =
                currentAssignedValue ||
                (currentAssignedValue === 0 &&
                  !record.key.includes("grantKill"))
                  ? currentAssignedValue
                  : isCurrentEditedCell
                    ? currentAssignedValue
                    : record.key === "grantKillPercentage"
                      ? "*"
                      : "";

              if (
                ["includeTaxes", "useVolitilityCalculator"].includes(record.key)
              ) {
                return (
                  <Switch
                    checked={value}
                    onClick={() => updateOptionParams(col.key, record, !value)}
                  />
                );
              }
              return renderEditableCell(
                value,
                record,
                col.key,
                record.dataType,
                updateOptionParams,
                setCurrentEditedCell,
                {},
                record.options,
              );
            },
          };
        }
        return col;
      }),
    [
      paramsColumns,
      updateOptionParams,
      renderEditableCell,
      awardCalculatorValues,
      currentEditedCell,
    ],
  );

  // // Appends the "addAward" Column to the end of the table if it doesn't exist
  useMemo(() => {
    if (!paramsColumns.some((item) => item.key === "addAward")) {
      setParamsColumns((prevColumns) => {
        const addAwardColumn = {
          title: (
            <div>
              <p style={{ textAlign: "center" }}>Add Award</p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  cursor="pointer"
                  type="link"
                  onClick={() => handleAddColumn()}
                >
                  {/* <p style={{ fontSize: 25 }}>+</p> */}
                  <img src={addSVG} alt="add" width={16} />
                </Button>
              </div>
            </div>
          ),
          key: "addAward",
          width: headless ? "0%" : "10%",
        };
        return [...prevColumns, addAwardColumn];
      });
    }
  }, [handleAddColumn, headless, paramsColumns]);

  // Define the column parameters for the table (but actual data is controlled by state: awardCalculatorValues)
  const dataSource = Object.values(paramTableRows)
    .filter(
      (row) => !includeTaxes || !(row?.isPreTaxValue && !row?.isAfterTaxValue),
    )
    .filter(
      (row) => includeTaxes || !(!row?.isPreTaxValue && row?.isAfterTaxValue),
    )
    .filter(
      (row) =>
        !row.optionsCalculatorOnly ||
        !(
          row.optionsCalculatorOnly &&
          calculatorPageSelected !== "Options Schedule"
        ),
    )
    .filter((row) => !abbreviatedTable || row.showInAbbreviatedTable)
    .filter(
      (row) =>
        !row.onlyShowInOptionAbbreviatedTable ||
        !abbreviatedTable ||
        !(
          row.onlyShowInOptionAbbreviatedTable &&
          calculatorPageSelected !== "Options Schedule"
        ),
    )
    .filter(
      (row) =>
        (calculatorPageSelected !== "Simulation & Strategies" &&
          !row.simulationTableOnly) ||
        (calculatorPageSelected === "Simulation & Strategies" &&
          (row.simulationTableOnly || row.showInSimulationTable)),
    )
    .map((row) => ({
      title: row.title,
      key: row.key,
      dataType: row.dataType,
      defaultValue:
        row?.defaultValue || row?.defaultValue === 0 ? row.defaultValue : "",
      isCalculatedValue: row?.isCalculatedValue || false,
      hideFromUser: row.hideFromUser,
      isPreTaxValue: row.isPreTaxValue,
      isAfterTaxValue: row.isAfterTaxValue,
      options: row.options,
      showInAbbreviatedTable: row.showInAbbreviatedTable, // For general info table shown on other Pages
      onlyShowInOptionAbbreviatedTable: row.onlyShowInOptionAbbreviatedTable,
      isKeyValue: row.isKeyValue,
    }));

  const astericksExists = useMemo(() => {
    return awardCalculatorValues.some((item) => item?.grantKillAmount === 0);
  }, [awardCalculatorValues]);

  const currentEquationValues = awardCalculatorValues.find(
    (item) => item.ID === trancheScheduleSelected.replace("Grant ", "grant"),
  )?.equationSolverValues;

  const currentAwardStrikePrice =
    awardCalculatorValues.find(
      (item) => item.ID === trancheScheduleSelected.replace("Grant ", "grant"),
    )?.strikePrice || 1;

  const blackScholesSolverData = useMemo(() => {
    if (!currentEquationValues) return [];
    return currentEquationValues.map((d) => ({
      x: d.price,
      y: d.grantKillAmountAtPrice,
      z: d.grantKillPercentage,
    }));
  }, [currentEquationValues]);

  const correlation0point = useMemo(() => {
    if (!blackScholesSolverData?.length) return;
    let crossingIndex = blackScholesSolverData.findIndex((d, i) => {
      const next = blackScholesSolverData[i + 1];
      return next && Math.sign(d.y) !== Math.sign(next.y);
    });

    const x0 = blackScholesSolverData[crossingIndex];
    const x1 = blackScholesSolverData[crossingIndex + 1];

    if (!x0 || !x1) return;

    const exactX = x0.x + ((0 - x0.y) * (x1.x - x0.x)) / (x1.y - x0.y);
    return exactX;
  }, [blackScholesSolverData]);

  const getPercentageOf0point = useCallback(
    (percentage) => {
      const killPercentNeeded = 100 - percentage;

      let crossingIndex = blackScholesSolverData.findIndex((d, i) => {
        const next = blackScholesSolverData[i + 1];
        return next && next.z <= killPercentNeeded;
      });

      const x0 = blackScholesSolverData[crossingIndex];
      const x1 = blackScholesSolverData[crossingIndex + 1];

      if (!x0 || !x1) return;

      const exactX =
        x0.x + ((killPercentNeeded - x0.z) * (x1.x - x0.x)) / (x1.z - x0.z);
      return exactX;
    },
    [blackScholesSolverData],
  );

  return hidden ? (
    <></>
  ) : (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Table
          columns={paramTableRowsRendered}
          dataSource={dataSource}
          pagination={false}
          style={{ width: headless ? "65%" : "100%", marginBottom: 20 }}
          rowClassName={(record) =>
            record?.hideFromUser
              ? "hidden-row"
              : record?.isKeyValue && (record?.isAfterTaxValue || !includeTaxes)
                ? "key-value"
                : "no-hover"
          }
          size="small"
          showHeader={!headless}
        />
      </div>
      {astericksExists && (
        <div style={{ marginTop: -10, marginLeft: 10, marginBottom: 10 }}>
          {trancheOptionAstericksMessage}
        </div>
      )}
      {awardTitle === "Option" && (
        <>
          <ResponsiveContainer width="100%" height={250}>
            <ScatterChart
              style={{
                width: "100%",
                maxWidth: "700px",
                maxHeight: "70vh",
                aspectRatio: 1.618,
              }}
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 45,
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                type="number"
                dataKey="x"
                tick={{ fill: "", fontSize: 13 }}
                tickLine={false}
                domain={[
                  Math.floor(currentAwardStrikePrice) - 1,
                  5 + Math.ceil(correlation0point),
                ]}
                allowDataOverflow
                tickFormatter={(val) => toDollar(val, 0, true)}
                label={{
                  value: "Stock Price ($), (Percent Realized, %)",
                  position: "insideBottom",
                  offset: -2,
                  fontSize: 13,
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                tick={{ fill: "", fontSize: 13 }}
                tickLine={false}
                width={50}
                tickFormatter={(val) => toDollar(val, 0, true)}
                label={{
                  value: "Per Unit Kill Amount ($)",
                  angle: 270,
                  offset: 0,
                  dy: 65,
                  position: "insideLeft",
                  fontSize: 13,
                }}
              />
              <ReferenceLine
                x={correlation0point}
                stroke="var(--primary-1)"
                label={{
                  value: `${toDollar(correlation0point, 2, true)} - Full Potential Realized`,
                  position: "insideBottomLeft",
                  fill: "var(--green)",
                  fontSize: 12,
                }}
              />
              {getPercentageOf0point(25) && (
                <ReferenceLine
                  x={getPercentageOf0point(25)}
                  stroke="var(--graph-stock-awards)"
                  label={{
                    value: `${toDollar(getPercentageOf0point(25), 2, true)} (25%)`,
                    position: "top",
                    fill: "var(--graph-stock-awards)",
                    fontSize: 11,
                  }}
                />
              )}
              {getPercentageOf0point(50) && (
                <ReferenceLine
                  x={getPercentageOf0point(50)}
                  stroke="var(--purple)"
                  label={{
                    value: `${toDollar(getPercentageOf0point(50), 2, true)} (50%)`,
                    position: "top",
                    fill: "var(--purple)",
                    fontSize: 11,
                  }}
                />
              )}
              {getPercentageOf0point(75) && (
                <ReferenceLine
                  x={getPercentageOf0point(75)}
                  stroke="var(--graph-bonus)"
                  label={{
                    value: `${toDollar(getPercentageOf0point(75), 2, true)} (75%)`,
                    position: "top",
                    fill: "var(--graph-bonus)",
                    fontSize: 11,
                  }}
                />
              )}
              {getPercentageOf0point(90) && (
                <ReferenceLine
                  x={getPercentageOf0point(90)}
                  stroke="var(--secondary-1)"
                  label={{
                    value: `${toDollar(getPercentageOf0point(90), 2, true)} (90%)`,
                    position: "top",
                    fill: "var(--secondary-1)",
                    fontSize: 11,
                  }}
                />
              )}

              <ReferenceLine
                x={currentAwardStrikePrice}
                stroke="var(--complement)"
                label={{
                  value: `${toDollar(currentAwardStrikePrice, 2, true)} - Strike Price`,
                  position: "insideBottomLeft",
                  fill: "var(--complement)",
                  fontSize: 12,
                }}
              />

              {!isNaN(parseFloat(generalInfoParams?.[0]?.sharePrice)) && (
                <ReferenceLine
                  x={parseFloat(generalInfoParams?.[0]?.sharePrice)}
                  stroke="var(--complement-1)"
                  label={{
                    value: `${toDollar(parseFloat(generalInfoParams?.[0]?.sharePrice), 2, true)} - Share Price`,
                    position: "insideBottomLeft",
                    fill: "var(--complement-1)",
                    fontSize: 12,
                  }}
                />
              )}

              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                // defaultIndex={defaultIndex}
              />
              <Scatter
                name="Kill Amount"
                data={blackScholesSolverData}
                fill="var(--secondary)"
                line
                lineJointType="monotone"
                shape={(props) => {
                  const { cx, cy } = props;
                  return <circle cx={cx} cy={cy} r={2} fill="black" />;
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
          <div>
            <h3 style={{ textAlign: "center", fontSize: 24 }}>
              {trancheScheduleSelected} Full Potential Realized at Share Price:{" "}
              {correlation0point
                ? toDollar(correlation0point, 2, true)
                : blackScholesSolverData?.length > 0 &&
                    blackScholesSolverData?.[blackScholesSolverData?.length - 1]
                      ?.y > 0
                  ? `>$${99 + (Math.floor(currentAwardStrikePrice) || 1)}`
                  : "N/A"}
            </h3>
          </div>
        </>
      )}
    </>
  );
}
